import { setNotification } from "features/Notification/notificationSlice";
import React, { useEffect, useState } from "react";
import { Dropdown, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";

export default function SelectMaterialList({
  selectedMaterial,
  setSelectedMaterial,
  materialListData,
  setMaterialListData,
  resultHeight,
  showModal,
}) {
  /*   All States
   ********************************************* */
  const dispatch = useDispatch();

  const [searchParams, setSearchParams] = useSearchParams();

  const [searchText, setSearchText] = useState("");
  const [searchFilterBy, setSearchFilterBy] = useState(null);
  const [loading, setLoading] = useState(null);

  /*   All Functions
   ********************************************* */
  const fetchMaterialListData = async (query = false) => {
    try {
      setLoading(true);

      let body = {
        filterBy: "all",
        page: 1,
        limit: 100,
      };
      if (query) {
        body = {
          ...body,
          query: query,
        };
      }

      const { data } = await API.post("/manufacturer/material-lists/", body, {
        headers: authHeader(),
      });
      if (data?.success) {
        setMaterialListData(data?.data);
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const updateSearchParams = (key, value) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (value) {
      newSearchParams.set(key, value);
    } else {
      newSearchParams.delete(key);
    }
    setSearchParams(newSearchParams);
    showModal(false);
  };

  const resetAll = () => {
    // Reset search params
    setSearchText("");
    setSearchFilterBy(null);
    setSelectedMaterial(null);
  };

  const TableBody = ({ material }) => (
    <tr
      className={`${
        selectedMaterial?.id === material.id && "bg-primary bg-opacity-10"
      } `}
      key={material.id}
    >
      <td className="align-middle py-1 ">{material.styleId}</td>
      <td className="align-middle py-1 ">
        {material?.description?.length > 20
          ? `${material?.description?.slice(0, 20)}...${material?.gsm}GSM`
          : `${material?.description} ${material?.gsm} GSM`}
      </td>
      <td className="align-middle py-1 ">{material.color}</td>
      <td className="align-middle py-1 ">
        <button
          className="btn btn-sm  btn-primary"
          onClick={() => updateSearchParams("materialId", material.materialId)}
        >
          Select
        </button>
      </td>
    </tr>
  );

  /*   All UseEffects
   ********************************************* */

  useEffect(() => {
    let timeout = setTimeout(() => {
      fetchMaterialListData();
    }, 50);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    // Check if materialId is present in search params
    const materialId = searchParams.get("materialId");
    if (materialId && materialListData) {
      const selectedMaterial = materialListData?.find(
        (material) => material.materialId === parseInt(materialId, 10)
      );
      if (selectedMaterial) {
        setSelectedMaterial(selectedMaterial);
      } else {
        setSelectedMaterial(null);
      }
    } else {
      setSelectedMaterial(null);
    }
  }, [searchParams, materialListData]);

  if (!materialListData || loading) {
    return (
      <div className="flex-center h-100">
        <Spinner variant="primary" />
      </div>
    );
  }

  return (
    <div className="h-100">
      <div className="">
        <div className="d-flex align-items-center justify-content-between">
          <h4 className="mb-2">Select Material List</h4>
          <div>
            <button onClick={resetAll} className="btn btn-sm btn-danger">
              Clear
            </button>
          </div>
        </div>
      </div>
      <div className="">
        {/* Search */}
        <div className="d-flex align-items-center gap-2 mb-3 mt-2">
          {/* Filter By */}
          <Dropdown>
            <Dropdown.Toggle>
              {searchFilterBy
                ? searchFilterBy.charAt(0).toUpperCase() +
                  searchFilterBy.slice(1)
                : "Filter By"}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => setSearchFilterBy(null)}>
                All
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setSearchFilterBy("styleId")}>
                Style Id
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setSearchFilterBy("description")}>
                Description
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setSearchFilterBy("color")}>
                Color
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setSearchFilterBy("gsm")}>
                GSM
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          <input
            type="text"
            value={searchText}
            autoFocus
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search Material List"
            className="form-control w-100"
          />
          <button
            className="btn btn-primary"
            onClick={() => fetchMaterialListData(searchText)}
          >
            Search
          </button>
        </div>

        {/* Selected Material */}
        {selectedMaterial && (
          <>
            <div className="mt-3 bg-success bg-opacity-25  px-2 rounded-3 d-flex align-items-center justify-content-between">
              <h4 className="">Selected </h4>
              <div className="d-flex align-items-center gap-2">
                <strong>Style Id:</strong> {selectedMaterial?.styleId}
                <strong>Color:</strong> {selectedMaterial?.color}
              </div>
              <div>
                {/* Delete */}
                <button
                  className="btn btn-sm px-1 py-0 btn-danger"
                  onClick={() => updateSearchParams("materialId", null)}
                >
                  <i className="mdi mdi-delete"></i>
                </button>
              </div>
            </div>
          </>
        )}

        {/* Material List */}
        <h4 className="mt-3">Material List</h4>

        {/* Table */}
        {materialListData?.length > 0 ? (
          <div
            className="table-responsive bg-white flex-grow-1"
            style={{
              overflowY: "auto",
              height: "50vh",
            }}
          >
            <table className="table table-bordered table-hover ">
              <thead>
                <tr>
                  <th className="text-nowrap">Style Id</th>
                  <th className="w-100">Description</th>
                  <th>Color</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody className="">
                {materialListData ? (
                  materialListData.map((material) => (
                    <TableBody key={material?.id} material={material} />
                  ))
                ) : (
                  <>
                    <tr>
                      <td colSpan={5} className="text-center">
                        No Material List Found
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center mt-5">
            <h5>No Material List Found</h5>
          </div>
        )}
      </div>
    </div>
  );
}
